import {
  ClassifierMappingFilter,
  DirectoryHeader,
  EMPTY_PAGE,
  InfoIconUrl,
  Main,
  Padding,
  TotalEntries,
  useDirectory,
  useListClassifierMappingQuery,
} from '@atlas-frontend/sdk';
import { keepPreviousData } from '@tanstack/react-query';
import { useEffect } from 'react';
import { path } from '../../paths';
import { useNavigateTo } from '../../paths/useNavigateTo';
import {
  DirectoryWrapper,
  FooterPagination,
} from './ClassifierMappingDirectory.styles';
import { ClassifierMappingDirectoryTable } from './ClassifierMappingDirectoryTable';
import translations from './ClassifierMappingDirectory.translations.json';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import { Helmet } from 'react-helmet-async';

const { addTranslations, useTranslation } = i18nNsFactory(
  'classifier-mapping-directory',
);
addTranslations(translations);

const FILTER_KEYS: (keyof ClassifierMappingFilter)[] = [
  'code',
  'name',
  'classifierFromCode',
  'classifierToCode',
  'description',
  'search',
];

export function ClassifierMappingDirectory() {
  const { t } = useTranslation();
  const navigateToClassifierMappingCreate = useNavigateTo(
    path.classifierMappingCreate,
  );

  const {
    headerProps,
    tableProps,
    paginationProps,
    queryParameters,
    setTotal,
  } = useDirectory({
    queryFilterKeys: FILTER_KEYS,
    initialSort: [{ id: 'code', desc: false }],
  });

  // Fetch classifiers
  const {
    data: { content: mappings, totalElements } = EMPTY_PAGE,
    isFetching: isClassifiersFetching,
  } = useListClassifierMappingQuery(queryParameters, {
    placeholderData: keepPreviousData,
  });

  useEffect(() => {
    setTotal(totalElements);
  }, [setTotal, totalElements]);

  return (
    <>
      <Helmet>
        <title>{t('title.directory')}</title>
      </Helmet>
      <Main>
        <Main.Col border={false}>
          <Main.Content
            padding={false}
            id="classifier-mapping-directory-content"
          >
            <DirectoryWrapper>
              <DirectoryHeader
                title={t('title.directory')}
                infoIconUrl={InfoIconUrl.CLASSIFIER_MAPPING_DIRECTORY}
                {...headerProps}
              />
              <Padding horizontal bottom>
                <ClassifierMappingDirectoryTable
                  data={mappings || []}
                  onCreate={navigateToClassifierMappingCreate}
                  isLoading={isClassifiersFetching}
                  {...tableProps}
                />
              </Padding>
            </DirectoryWrapper>
          </Main.Content>
          <Main.Footer>
            <TotalEntries count={totalElements} />
            <FooterPagination {...paginationProps} />
          </Main.Footer>
        </Main.Col>
      </Main>
    </>
  );
}
